import React, { useContext, useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { ThemeContext } from '../contexts/ThemeContext';
import { Box, Typography, Button, Modal, Paper } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Képek és logó importálása (győződj meg róla, hogy az útvonalak helyesek!)
import agykontrollBg1 from '../assets/images/agy1.jpg';
import agykontrollBg2 from '../assets/images/agy2.jpg';
import agykontrollBg3 from '../assets/images/agy3.jpg';
import agykontrollBg4 from '../assets/images/agy4.jpg';
import agykontrollBg5 from '../assets/images/agy5.jpg';
import agykontrollBg6 from '../assets/images/agy6.jpg';
import StarImage from "../assets/images/logo.jpeg";

// Importáljuk a TestimonialsSlider komponenst és a Testimonial interface-t
import TestimonialsSlider, { Testimonial } from '../components/TestimonialsSlider';
import SocialMediaSidebar from '../components/SocialMediaSidebar';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import { BRAND, brandPalette } from '../theme/brand';
import TopicIcon from '../components/TopicIcons';

// Az Agykontroll oldal specifikus véleményei
const agykontrollTestimonials: Testimonial[] = [
  {
    quote: 'A gyermekagykontroll képzés hihetetlenül sokat segített a fiamnak a koncentrációban és a stresszkezelésben. Azóta sokkal nyugodtabb és kiegyensúlyozottabb!',
    author: 'Éva',
    title: 'Szülő'
  },
  {
    quote: 'Eleinte szkeptikus voltam, de a kislányom az első hétvége után sokkal magabiztosabb lett az iskolában. Ez tényleg csoda!',
    author: 'Gábor',
    title: 'Szülő'
  },
  {
    quote: 'Oktatóként is lenyűgöző volt látni, ahogy a gyerekek milyen gyorsan elsajátítják ezeket a technikákat. Életre szóló tudás!',
    author: 'Andrea',
    title: 'Pedagógus'
  },
  {
    quote: 'Most már tudom, hogyan kell megnyugtatni magam, ha izgulok a dolgozat előtt. Köszönöm! A technikák egyszerűek és könnyen alkalmazhatók a mindennapokban.',
    author: 'Bence',
    title: '8 éves diák'
  },
  {
    quote: 'Soha nem gondoltam volna, hogy ennyire könnyű a tanulás, ha az agykontroll módszereit használom. A jegyeim is javultak! Még a kedvem is jobb lett tőle.',
    author: 'Zsófi',
    title: '11 éves diák'
  },
  {
    quote: 'A szülőknek is rengeteget ad a képzés. Jobban megértjük a gyerekeinket, és mi is nyugodtabbá válunk a mindennapokban.',
    author: 'Mária',
    title: 'Szülő és résztvevő'
  },
];


// Az API alap-címe: éles oldalon üres (az nginx továbbítja az /api kéréseket), fejlesztéskor a REACT_APP_API_URL-ből jön
const API = process.env.REACT_APP_API_URL ?? "";

// Modal stílusa
const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', md: 600 },
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  border: '2px solid #6D1B77',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const AgykontrollPage = () => {
    const { darkMode } = useContext(ThemeContext)!;
    const pal = brandPalette(darkMode);
    const [open, setOpen] = useState(false); // Modal state
    const [startDate, setStartDate] = useState('Betöltés...');
    const [endDate, setEndDate] = useState('');
    const [location, setLocation] = useState(''); 
    const [eventUrl, setEventUrl] = useState(''); 

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    // Az API hívás a dátum lekérésére a fetch-csel
    useEffect(() => {
        const fetchNextDate = async () => {
            try {
                const response = await fetch(`${API}/api/user/getidopont`);
                if (!response.ok) {
                    throw new Error(`HTTP hiba! Státusz: ${response.status}`);
                }
                const data = await response.json();
                setStartDate(data.idopont1);
                setEndDate(data.idopont2);
                setLocation(data.location);
                setEventUrl(data.eventUrl); 
            } catch (error) {
                console.error("Hiba az időpont lekérésekor:", error);
                setStartDate('Nincs elérhető időpont'); // Fallback üzenet hiba esetén
                setEndDate('');
            }
        };
        fetchNextDate();
    }, []);

  const firstAgykontrollSliderTestimonials = agykontrollTestimonials.slice(0, 3);
  const secondAgykontrollSliderTestimonials = agykontrollTestimonials.slice(3, 6);

  // ... (a ContentSection és a BackgroundParallaxSection komponensek változatlanok)
  interface ContentSectionProps {
    children: React.ReactNode;
    id: string;
    icon?: string;
  }

  interface BackgroundParallaxSectionProps {
    children?: React.ReactNode;
    backgroundImage: string;
    id: string;
    minHeight?: string;
  }

  const ContentSection = ({ children, id, icon }: ContentSectionProps) => (
    <Box
      id={id}
      sx={{
        width: '100%',
        backgroundColor: darkMode ? '#1a1048' : '#F7F0F9',
        py: { xs: 6, md: 8 },
        pb: { xs: 12, md: 12 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        color: pal.text,
        position: 'relative',
        zIndex: 1,
        textAlign: 'center',
      }}
    >
      <Box sx={{ maxWidth: 'lg', width: '100%', px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, flexDirection: 'column' }}>
          {icon && (
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center', filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.35))', animation: 'iconBob 3.2s ease-in-out infinite', '@keyframes iconBob': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-6px)' } } }}>
              <TopicIcon id={icon} size={72} />
            </Box>
          )}
          <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const BackgroundParallaxSection = ({ children, backgroundImage, id, minHeight = '500px' }: BackgroundParallaxSectionProps) => {
    return (
      <Box
        id={id}
        sx={{
          width: '100%',
          backgroundImage: `url(${backgroundImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          minHeight: minHeight,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative',
          marginTop: '-50px',
          zIndex: -1,
        }}
      >
        {children}
      </Box>
    );
  };

  return (
    <Box sx={{ pt: { xs: '56px', md: '64px' } }}>
      <Navbar />
      <SocialMediaSidebar />

      {/* Hero szekció - Szöveges Sáv */}
      <ContentSection
        id="hero-section"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: { xs: 4, md: 8 },
            textAlign: { xs: 'center', md: 'left' },
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flex: 3 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: pal.heading,
                mb: 2,
                fontSize: { xs: '1.9rem', sm: '2.4rem', md: '3.5rem' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Gyermekagykontroll – Lásd magadban a csodát!
            </Typography>
            <Typography
              variant="h6"
              paragraph
              sx={{
                lineHeight: 1.6,
                color: pal.text,
                fontSize: { xs: '1rem', md: '1.25rem' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Programunk segíti a gyerekeket abban, hogy játékosan fejlesszék koncentrációjukat, önbizalmukat és belső erőforrásaikat. A foglalkozásokat életkorhoz igazított módszerekkel tartjuk, hogy minden gyermek a saját tempójában fejlődhessen.
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 3,
                px: 5,
                py: 1.5,
                borderRadius: 25,
                fontWeight: 'bold',
                textTransform: 'none',
                backgroundColor: pal.btnBg,
                color: pal.btnText,
                '&:hover': { backgroundColor: pal.btnHoverBg, color: darkMode ? BRAND.purpleDark : '#FFFFFF' },
              }}
              component="a"
              href="#contact-section"
            >
              Tudj meg többet! <ArrowForwardIcon sx={{ ml: 1 }} />
            </Button>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src={StarImage}
              alt="Gyermekagykontroll"
              sx={{
                maxWidth: { xs: '200px', md: '300px' },
                height: 'auto',
                borderRadius: 2,
              }}
            />
          </Box>
        </Box>
        <Button
    component="a" // A gombot linkként kezeli
    href={eventUrl || '#'} 
    target="_blank"
    rel="noopener noreferrer"
    sx={{
        mt: 3,
        p: 1.5,
        borderRadius: '20px',
        backgroundColor: BRAND.purpleLight,
        color: '#fff',
        fontWeight: 'bold',
        textTransform: 'none', // Kisbetűs szöveg használata
        width: 'fit-content',
        mx: 'auto',
        justifyContent: 'center',
        textAlign: 'center',
        animation: 'pulse 2s infinite',
        '&:hover': {
            backgroundColor: BRAND.purpleLight, // A szín maradjon ugyanaz hover-nél is
            boxShadow: '0 0 0 10px rgba(201, 43, 181, 0)',
        },
        '@keyframes pulse': {
            '0%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 0 rgba(201, 43, 181, 0.7)',
            },
            '70%': {
                transform: 'scale(1.05)',
                boxShadow: '0 0 0 10px rgba(201, 43, 181, 0)',
            },
            '100%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 0 rgba(201, 43, 181, 0)',
            },
        },
    }}
>
    <Typography variant="body1">
          Következő időpont: {startDate}
          {endDate && ` - ` + endDate}
          {location && ` - ` + location}
        </Typography>
</Button>
      </ContentSection>

      <BackgroundParallaxSection
        backgroundImage={agykontrollBg1}
        id="parallax-separator-1"
        minHeight="500px"
      />
      <ContentSection
        id="about-section"
        icon="konyv"
      >
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: pal.heading }}>
          Bemutatkozó szöveg – Gyermekagykontroll képzéshez
        </Typography>
        <Typography paragraph sx={{ pl: 2, '& li': { mb: 1, display: 'flex', alignItems: 'flex-start' }, textAlign: 'left' }}>
          2008. július 1-jén életem új fordulatot vett – sikeres vizsgát tettem, gyermekagykontroll-oktató lettem.
          Azóta számos képzést tartottam, de ünnepnap a szívemnek, amikor csoportonként 10–20 gyermek és szüleik bizalommal és izgalommal vegyesen velem töltenek egy hétvégét.
        </Typography>
        <Typography paragraph sx={{ pl: 2, '& li': { mb: 1, display: 'flex', alignItems: 'flex-start' }, textAlign: 'left' }}>
          Két nap – ami egy felszólítással indul: „Lásd magadban a csodát!” És ezért a csodáért mindent meg is teszünk.
          A képzés életre szóló élményt ad – olyan eszközöket, amelyek nemcsak most, hanem a legnehezebb helyzetekben is azonnal használhatóak.
          A gyermekagykontroll nem varázslat – de a jelenlévők gyakran azt mondják: „Ez csodálatos volt.”
        </Typography>
        <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: pal.heading }}>
          A két nap során mindenki megtanulja:
        </Typography>
        <Box component="ul" sx={{ pl: 2, '& li': { mb: 1, display: 'flex', alignItems: 'flex-start' }, textAlign: 'left' }}>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>hogyan csillapíthatja a belső feszültségét,</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>hogyan tudja megnyugtatni magát,</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>hogyan élhet kiegyensúlyozottabb, boldogabb életet,</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>hogyan tanuljon másképp – örömmel és valódi sikerekkel az iskolában és azon túl is.</Typography></li>
        </Box>
        <Typography paragraph sx={{ mt: 3 }}>
          A szavakat is újraértelmezzük:
        </Typography>
        <Box component="ul" sx={{ pl: 2, '& li': { mb: 1, display: 'flex', alignItems: 'flex-start' }, textAlign: 'left' }}>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Az elvárás helyét átveszi a kíváncsiság.</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>A „nehézségekből” izgalmas kihívások lesznek.</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Az értékelés helyét megerősítések váltják fel.</Typography></li>
        </Box>
        <Typography paragraph sx={{ pl: 2, '& li': { mb: 1, display: 'flex', alignItems: 'flex-start' }, textAlign: 'left' }}>
          A rossz gondolatokat pedig alapos „nagytakarítással” alakítjuk át, önbizalmat adó, bátorító mondatokká.
          Közben a gyermekek – és a képzésen jelen lévő szülők is – megtapasztalják azt, amit felnőttként is sokan még csak keresnek: a figyelem irányítását, a belső biztonság érzését és az önmagukba vetett hitet.
        </Typography>
      </ContentSection>

      <BackgroundParallaxSection
        backgroundImage={agykontrollBg2}
        id="parallax-separator-2"
        minHeight="500px"
      />
      <ContentSection
        id="target-audience-section"
        icon="celpont"
      >
        <Box
          sx={{
            padding: 3,
            borderRadius: 2,
            color: pal.text,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: pal.heading }}>
            A gyermekagykontroll képzés nektek szól:
          </Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1, display: 'flex', alignItems: 'flex-start' }, textAlign: 'left' }}>
            <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>ha a gyermeked nehezen koncentrál, túl sok minden jár a fejében,</Typography></li>
            <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>ha gyakran szorong, izgul, vagy esténként sokáig forgolódik elalvás előtt,</Typography></li>
            <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>ha szeretnéd, hogy megtanulja megnyugtatni magát, irányítani a gondolatait,</Typography></li>
            <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>ha úgy érzed, tele van belső kincsekkel, de még nem tudja, hogyan használja őket.</Typography></li>
          </Box>
        </Box>
      </ContentSection>

      <BackgroundParallaxSection
        backgroundImage={agykontrollBg3}
        id="parallax-separator-3"
        minHeight="500px"
      />
      <ContentSection
        id="what-happens-section"
        icon="agy"
      >
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: pal.heading }}>
          Mi történik a képzésen?
        </Typography>
        <Typography paragraph>
          Ez a két nap nem iskola – hanem egy élményekkel teli belső utazás.
        </Typography>
        <Box component="ul" sx={{ pl: 2, '& li': { mb: 1, display: 'flex', alignItems: 'flex-start' }, textAlign: 'left' }}>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Megtanuljuk és begyakoroljuk, hogyan csillapítsuk a szorongást és az izgalmat</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Relaxációs technikákat sajátítunk el</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Megismerkedünk a pozitív gondolkodással</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Felfedezzük, hogyan működik a képzelet – és megtanuljuk használni</Typography></li>
          <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Játszunk, rajzolunk, mesélünk, és közben formálódik a belső biztonságérzetünk</Typography></li>
        </Box>
      </ContentSection>

      <BackgroundParallaxSection
        backgroundImage={agykontrollBg4}
        id="parallax-separator-4"
        minHeight="500px"
      />
      <ContentSection
        id="what-you-take-away-section"
        icon="ajandek"
      >
        <Box
          sx={{
            padding: 3,
            borderRadius: 2,
            color: pal.text,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: pal.heading }}>
            Mit visztek haza?
          </Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1, display: 'flex', alignItems: 'flex-start' }, textAlign: 'left' }}>
            <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Belső nyugalmat, amihez bármikor visszanyúlhattok</Typography></li>
            <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>Azonnal működő technikákat, amik magabiztosságot és kiegyensúlyozottságot adnak</Typography></li>
            <li><ArrowForwardIcon sx={{ mr: 1, mt: 0.5, color: pal.accent }} /><Typography>A biztos érzést: „Én ezt meg tudom csinálni.”</Typography></li>
          </Box>
        </Box>
      </ContentSection>

      <BackgroundParallaxSection
        backgroundImage={agykontrollBg5}
        id="parallax-separator-5"
        minHeight="500px"
      />
      <ContentSection
        id="testimonials-section"
        icon="beszed"
      >
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: pal.heading,
            mb: 4,
            textAlign: 'center',
          }}
        >
          Diákjaink és Szüleik mondták rólunk:
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            width: '100%',
            justifyContent: 'center',
            alignItems: 'stretch',
          }}
        >
          {/* Első véleménydoboz (3 véleménnyel) */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: pal.card,
              border: pal.cardBorder,
              boxShadow: pal.cardShadow,
              padding: 3,
              borderRadius: 2,
              minHeight: '250px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              color: pal.text,
            }}
          >
            <TestimonialsSlider testimonials={firstAgykontrollSliderTestimonials} autoScroll={true} interval={5000} />
          </Box>

          {/* Második véleménydoboz (3 véleménnyel) */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: pal.card,
              border: pal.cardBorder,
              boxShadow: pal.cardShadow,
              padding: 3,
              borderRadius: 2,
              minHeight: '250px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              color: pal.text,
            }}
          >
            <TestimonialsSlider testimonials={secondAgykontrollSliderTestimonials} autoScroll={true} interval={5000} />
          </Box>
        </Box>
      </ContentSection>

      <BackgroundParallaxSection
        backgroundImage={agykontrollBg6}
        id="parallax-separator-6"
        minHeight="500px"
      />

      {/* Kontakt szekció: a közös ContactSection komponens (az id="contact-section" a komponensben van) */}
      <ContentSection id="contact-wrapper">
        <ContactSection handleOpen={handleOpen} />
      </ContentSection>

      {/* Modal ablak a képzésekhez */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="course-modal-title"
        aria-describedby="course-modal-description"
        disableScrollLock
      >
        <Paper sx={modalStyle}>

          {/* Következő képzések */}
          <Typography variant="h6" component="h3" sx={{ mt: 3, fontWeight: 'bold', color: pal.heading }}>
            Következő képzés
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {startDate !== 'Nincs elérhető időpont' && (
              <Box sx={{ p: 2, border: pal.cardBorder, borderRadius: 1, backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : '#F7F0F9' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: pal.text }}>
                  Dátum: {startDate} {endDate && `- ${endDate}`}
                </Typography>
                <Typography variant="body2" sx={{ color: pal.text }}>
                  Helyszín: {location}
                </Typography>
                <Typography variant="body2" sx={{ color: pal.text }}>
                  Információ: <a href={eventUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>Facebook esemény</a>
                </Typography>
              </Box>
            )}
            {startDate === 'Nincs elérhető időpont' && (
              <Typography variant="body1" sx={{ color: pal.text }}>
                {startDate}
              </Typography>
            )}
        </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={handleClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: pal.btnBg, color: pal.btnText, '&:hover': { backgroundColor: pal.btnHoverBg } }}>
              Bezár
            </Button>
          </Box>
        </Paper>
      </Modal>

      <Footer />
    </Box>
  );
};

export default AgykontrollPage;